import { Injectable } from '@angular/core';
import { DataRepository, RxdbIdProvider, TableRepository } from '@database/workspace';
import { Observable, catchError, combineLatest, firstValueFrom, map, of, switchMap } from 'rxjs';
import { BusinessLayerService, StandardTableProvider } from '@domain/business-layer';
import { QueryParamsProvider } from '@domain/routing';
import { DataModel, PrimaryKeyValueModel, TableModel, ToastDuration, ToastService } from '@shared';

@Injectable()
export class NewFromRecordService {
	private workfileId: string;
	private entryId: number;
	private entityName: string;

	public constructor(
		private dataRepository: DataRepository,
		private tableRepository: TableRepository,
		private businessLayerService: BusinessLayerService,
		private queryParamsProvider: QueryParamsProvider,
		private rxdbIdProvider: RxdbIdProvider,
		private standardTableProvider: StandardTableProvider,
		private toastService: ToastService
	) {
		this.workfileId = this.queryParamsProvider.workfileId;
		this.entityName = this.queryParamsProvider.parentTableName;
		this.entryId = Number(this.queryParamsProvider.entryId);
	}

	public async handleNewFromRecord(newParentRecord: DataModel): Promise<void> {
		const childTablesMetadata = await firstValueFrom(this.fetchChildrenTables$());
		const childTableRecordsDictionary: { [tableName: string]: DataModel[] } = {};
		let currentRxdbId = await this.rxdbIdProvider.getCurrentRxdbId(this.workfileId);

		const primaryKeysAndValues = await firstValueFrom(
			this.businessLayerService.getPrimaryKeyAndValue$(
				this.workfileId,
				this.queryParamsProvider.projectNumber,
				this.entityName,
				this.entryId
			)
		);
		const newPrimaryKeysAndValues = primaryKeysAndValues.map((primaryKeyAndValue: PrimaryKeyValueModel) => ({
			primaryKey: primaryKeyAndValue.primaryKey,
			primaryKeyValue: newParentRecord[primaryKeyAndValue.primaryKey]
		})) as PrimaryKeyValueModel[];

		for (const childTableMetadata of childTablesMetadata) {
			const childTableRecords: DataModel[] = await firstValueFrom(
				this.dataRepository.getChildTableData$(
					childTableMetadata.table_name,
					this.queryParamsProvider.projectNumber,
					primaryKeysAndValues
				)
			);

			const newChildTableRecords: DataModel[] = childTableRecords.map((childTableData: DataModel) => {
				currentRxdbId++;
				return this.mapChildDataIntoNewChild(childTableData, newPrimaryKeysAndValues, currentRxdbId);
			});

			childTableRecordsDictionary[childTableMetadata.table_name] = newChildTableRecords;
		}

		await this.rxdbIdProvider.setRxdbId(this.workfileId, currentRxdbId);
		await this.dataRepository.bulkInsert(childTableRecordsDictionary);
		await this.dataRepository.insert(newParentRecord, this.entityName, this.workfileId);
	}

	private fetchChildrenTables$(): Observable<TableModel[]> {
		return this.tableRepository.getTableChildIdsByName$(this.entityName).pipe(
			switchMap((childTableIds: number[] | null) => {
				if (!childTableIds) {
					return of([]);
				}

				return combineLatest([
					this.tableRepository.getTableMetadata$(this.standardTableProvider.standardTable ?? ''),
					this.tableRepository.getChildTablesMetadataByIds$(childTableIds)
				]).pipe(
					map(([standardTable, childTables]: [TableModel | null, TableModel[] | null]) =>
						[standardTable, ...(childTables ?? [])].filter((tableModel: TableModel) => tableModel)
					)
				);
			}),
			catchError((error) =>
				this.toastService.presentError(`${error}: Fetching child tables failed`, ToastDuration.Slow)
			)
		);
	}

	private mapChildDataIntoNewChild(
		originalChild: DataModel,
		primaryKeysAndValues: PrimaryKeyValueModel[],
		newRxdbId: number
	): DataModel {
		const clonedChild: DataModel = { ...originalChild };
		primaryKeysAndValues.forEach(({ primaryKey, primaryKeyValue }) => (clonedChild[primaryKey] = primaryKeyValue));

		clonedChild.rxdbid = newRxdbId;
		clonedChild.rxdbidstring = newRxdbId.toString();

		return clonedChild;
	}
}
